import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';
import { useEntitiesStore } from './entities';
import { isShadeDevice } from '../utils/shadeDevices';
import type { Device } from '@/types';

export type ShadesViewMode = 'rooms' | 'types' | 'groups';
export type ShadeTypeKey = 'tilt' | 'position' | 'openclose';
export type ShadeGroupCapability = 'all' | 'position' | 'tilt';
export type ShadeScopeKind = 'all' | 'room' | 'type' | 'group';
export type ShadeAction = 'open' | 'close' | 'stop';
export type ShadeSeasonSelectionMode = 'AUTO' | 'MANUAL';

export interface ShadesManagedDevice {
  id: string;
  deviceId: string;
  enabled: boolean;
  sortOrder: number;
}

export interface ShadeGroup {
  id: string;
  name: string;
  icon: string;
  capability: ShadeGroupCapability;
  enabled: boolean;
  sortOrder: number;
  deviceIds: string[];
}

export interface ShadeCommandPayload {
  action?: ShadeAction | null;
  /** Target position (0 = open, 100 = closed) */
  position?: number | null;
  /** Target tilt / slat angle (0-100) */
  tilt?: number | null;
}

export interface ShadePreset {
  id: string;
  name: string;
  icon: string;
  scopeKind: ShadeScopeKind;
  scopeId: string | null;
  command: ShadeCommandPayload;
  sortOrder: number;
}

export interface ShadeSeason {
  id: string;
  name: string;
  icon: string;
  sortOrder: number;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
}

export interface ShadeCommandScope {
  kind: ShadeScopeKind;
  id?: string | null;
}

interface ShadesSettingsPayload {
  hiddenDeviceIds: string[];
  devices: ShadesManagedDevice[];
  groups: ShadeGroup[];
  presets: ShadePreset[];
  seasons: ShadeSeason[];
  seasonSelectionMode: ShadeSeasonSelectionMode;
  manualSeasonId: string | null;
  lastView: ShadesViewMode;
}

interface ShadeEntry {
  managed: ShadesManagedDevice;
  device: Device;
  shadeType: ShadeTypeKey;
}

export const SHADE_TYPE_ORDER: ShadeTypeKey[] = ['tilt', 'position', 'openclose'];

const SHADE_TYPE_ICONS: Record<ShadeTypeKey, string> = {
  tilt: 'blinds',
  position: 'roller_shades',
  openclose: 'roller_shades_closed',
};

/**
 * A shade type and a group capability as an English/Slovene pair — the shape
 * the app's inline copy uses (`consoleText.ts`). The getters below pick their
 * half so every surface follows a language switch through one table.
 */
const SHADE_TYPE_LABELS: Record<ShadeTypeKey, [string, string]> = {
  tilt: ['Position & Tilt', 'Pozicija in nagib'],
  position: ['Position', 'Pozicija'],
  openclose: ['Open / Close', 'Odpri / Zapri'],
};

const SHADE_GROUP_CAPABILITY_LABELS: Record<ShadeGroupCapability, [string, string]> = {
  all: ['Any shade', 'Vsako senčilo'],
  position: ['Position', 'Pozicija'],
  tilt: ['Tilt', 'Nagib'],
};

const SHADE_VALUE_KEYS = {
  position: ['position', 'position_status', 'level'],
  tilt: ['tilt', 'slat', 'slat_position', 'orientation', 'angle'],
  updown: ['updown', 'up_down'],
  stop: ['stop'],
} as const;

function normalizeKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** A device value's key and name, as the shade matchers read them. */
interface DeviceValueLike {
  valueKey: string;
  name?: string | null;
  currentValue?: unknown;
}

function isDeviceValueLike(row: unknown): row is DeviceValueLike {
  return typeof row === 'object' && row !== null && 'valueKey' in row && typeof row.valueKey === 'string';
}

function getDeviceValues(device: Device): DeviceValueLike[] {
  if (Array.isArray(device.deviceValues)) return device.deviceValues;
  // Older hub payloads carried the same rows under the legacy `values` alias.
  const legacy = 'values' in device ? device.values : undefined;
  return Array.isArray(legacy) ? legacy.filter(isDeviceValueLike) : [];
}

function hasValueKey(device: Device, keys: readonly string[]): boolean {
  const normalizedKeys = new Set(keys.map(normalizeKey));
  return getDeviceValues(device).some((value) => {
    const valueKey = normalizeKey(value.valueKey);
    const valueName = normalizeKey(value.name ?? '');
    return normalizedKeys.has(valueKey) || normalizedKeys.has(valueName);
  });
}

function buildId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function compareDevices(a: Device, b: Device): number {
  return (
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
    a.deviceName.localeCompare(b.deviceName, undefined, { sensitivity: 'base' })
  );
}

export function getShadeCapabilities(device: Device): {
  position: boolean;
  tilt: boolean;
  updown: boolean;
  stop: boolean;
} {
  return {
    position: hasValueKey(device, SHADE_VALUE_KEYS.position),
    tilt: hasValueKey(device, SHADE_VALUE_KEYS.tilt),
    updown: hasValueKey(device, SHADE_VALUE_KEYS.updown),
    stop: hasValueKey(device, SHADE_VALUE_KEYS.stop),
  };
}

export function supportsGroupCapability(device: Device, capability: ShadeGroupCapability): boolean {
  const features = getShadeCapabilities(device);
  switch (capability) {
    case 'position':
      return features.position || features.updown;
    case 'tilt':
      return features.tilt;
    default:
      return true;
  }
}

export function detectShadeType(device: Device): ShadeTypeKey {
  const features = getShadeCapabilities(device);
  if (features.tilt) return 'tilt';
  if (features.position) return 'position';
  return 'openclose';
}

/** The cover's current position (0 = open, 100 = closed), null without feedback. */
export function getShadePosition(device: Device): number | null {
  return getShadePercent(device, SHADE_VALUE_KEYS.position);
}

/** The cover's current tilt percentage (0 = open, 100 = closed), null for covers
 *  that do not report one (zip screens, shutters) — the live shade layer's half
 *  of the same matchers (`bimShadeSpec.ts` feeds these into the slat angle). */
export function getShadeTilt(device: Device): number | null {
  return getShadePercent(device, SHADE_VALUE_KEYS.tilt);
}

function getShadePercent(device: Device, keys: readonly string[]): number | null {
  const value = getDeviceValues(device).find((row) => {
    const key = normalizeKey(row.valueKey);
    return keys.some((candidate) => key === normalizeKey(candidate));
  });
  const current = value?.currentValue;
  return typeof current === 'number' && Number.isFinite(current) ? Math.round(current) : null;
}

/** True when the shade reports being (nearly) closed. */
export function isShadeClosed(device: Device): boolean {
  const position = getShadePosition(device);
  return position !== null && position >= 50;
}

export function getShadeTypeIcon(type: ShadeTypeKey): string {
  return SHADE_TYPE_ICONS[type] ?? 'roller_shades';
}

export function getShadeTypeLabel(type: ShadeTypeKey): string {
  const pair = SHADE_TYPE_LABELS[type];
  return pair ? tt(pair[0], pair[1]) : tt('Shades', 'Senčila');
}

export function getShadeGroupCapabilityLabel(capability: ShadeGroupCapability): string {
  const pair = SHADE_GROUP_CAPABILITY_LABELS[capability];
  return pair ? tt(pair[0], pair[1]) : tt('Any shade', 'Vsako senčilo');
}

/**
 * One row of the hub's shades-settings payload. Only the fields the sanitizers
 * below read are named — the rest of the row stays as the hub sent it.
 */
interface RawShadeSettingsRow {
  id?: string;
  deviceId?: string;
  name?: string;
  icon?: string;
  enabled?: unknown;
  sortOrder?: unknown;
  capability?: string;
  scopeKind?: string;
  scopeId?: unknown;
  deviceIds?: unknown;
  command?: { action?: unknown; position?: unknown; tilt?: unknown };
  startMonth?: unknown;
  startDay?: unknown;
  endMonth?: unknown;
  endDay?: unknown;
}

const GROUP_CAPABILITIES: readonly ShadeGroupCapability[] = ['all', 'position', 'tilt'];
const PRESET_SCOPE_KINDS: readonly ShadeScopeKind[] = ['all', 'room', 'type', 'group'];
const SHADE_ACTIONS: readonly ShadeAction[] = ['open', 'close', 'stop'];
const SHADE_VIEW_KEYS = ['rooms', 'types', 'groups'] as const;

/** The row's value when it is one of `allowed`, else undefined. */
function readOneOf<T extends string>(value: string | undefined, allowed: readonly T[]): T | undefined {
  return allowed.find((option) => option === value);
}

/** The row's sort order when it is a finite number, else undefined. */
function readSortOrder(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readPercent(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : null;
}

/** What `/shades/command` answers: how the command landed per device. */
interface ShadeCommandResult {
  succeededDevices: number;
  targetedDevices: number;
  skippedDevices: number;
  [key: string]: unknown;
}

function sanitizeCommand(input: RawShadeSettingsRow['command']): ShadeCommandPayload {
  const record = input ?? {};
  return {
    action: typeof record.action === 'string' ? (readOneOf(record.action, SHADE_ACTIONS) ?? null) : null,
    position: readPercent(record.position),
    tilt: readPercent(record.tilt),
  };
}

function sanitizeManagedDevices(input: unknown): ShadesManagedDevice[] {
  return Array.isArray(input)
    ? input
        .map((item: RawShadeSettingsRow, index) => ({
          id: item.id ?? item.deviceId ?? buildId('shade-device'),
          deviceId: item.deviceId ?? item.id ?? '',
          enabled: item.enabled !== false,
          sortOrder: readSortOrder(item.sortOrder) ?? index,
        }))
        .filter((item) => item.deviceId.length > 0)
    : [];
}

function sanitizeGroups(input: unknown): ShadeGroup[] {
  return Array.isArray(input)
    ? input.map((item: RawShadeSettingsRow, index) => ({
        id: item.id ?? buildId('shade-group'),
        name: item.name ?? tt(`Shade Group ${index + 1}`, `Skupina senčil ${index + 1}`),
        icon: item.icon ?? 'roller_shades',
        capability: readOneOf(item.capability, GROUP_CAPABILITIES) ?? 'all',
        enabled: item.enabled !== false,
        sortOrder: readSortOrder(item.sortOrder) ?? index,
        deviceIds: Array.isArray(item.deviceIds)
          ? item.deviceIds.filter((value: unknown): value is string => typeof value === 'string')
          : [],
      }))
    : [];
}

function sanitizePresets(input: unknown): ShadePreset[] {
  return Array.isArray(input)
    ? input.map((item: RawShadeSettingsRow, index) => ({
        id: item.id ?? buildId('shade-preset'),
        name: item.name ?? tt(`Preset ${index + 1}`, `Prednastavitev ${index + 1}`),
        icon: item.icon ?? 'roller_shades',
        scopeKind: readOneOf(item.scopeKind, PRESET_SCOPE_KINDS) ?? 'all',
        scopeId: typeof item.scopeId === 'string' ? item.scopeId : null,
        command: sanitizeCommand(item.command),
        sortOrder: readSortOrder(item.sortOrder) ?? index,
      }))
    : [];
}

function sanitizeSeasons(input: unknown): ShadeSeason[] {
  if (!Array.isArray(input)) return [];
  return input.map((item: RawShadeSettingsRow, index) => ({
    id: item.id ?? buildId('shade-season'),
    name: item.name ?? tt(`Season ${index + 1}`, `Sezona ${index + 1}`),
    icon: item.icon ?? 'calendar_month',
    sortOrder: readSortOrder(item.sortOrder) ?? index,
    startMonth: typeof item.startMonth === 'number' ? item.startMonth : 1,
    startDay: typeof item.startDay === 'number' ? item.startDay : 1,
    endMonth: typeof item.endMonth === 'number' ? item.endMonth : 12,
    endDay: typeof item.endDay === 'number' ? item.endDay : 31,
  }));
}

export const useShadesStore = defineStore('shades', () => {
  const entitiesStore = useEntitiesStore();

  const hiddenDeviceIds = ref<string[]>([]);
  const managedDevices = ref<ShadesManagedDevice[]>([]);
  const groups = ref<ShadeGroup[]>([]);
  const presets = ref<ShadePreset[]>([]);
  const seasons = ref<ShadeSeason[]>([]);
  const seasonSelectionMode = ref<ShadeSeasonSelectionMode>('AUTO');
  const manualSeasonId = ref<string | null>(null);
  const activeSeasonId = ref<string | null>(null);
  const lastView = ref<ShadesViewMode>('rooms');
  const settingsLoading = ref(false);
  const settingsSaving = ref(false);
  const error = ref<string | null>(null);

  const activeSeason = computed(() => seasons.value.find((season) => season.id === activeSeasonId.value) ?? null);

  const autoDetectedShadeDevices = computed<Device[]>(() =>
    entitiesStore.devices.filter(isShadeDevice).sort(compareDevices),
  );

  function buildSeedDevices(): ShadesManagedDevice[] {
    return autoDetectedShadeDevices.value.map((device, index) => ({
      id: device.id,
      deviceId: device.id,
      enabled: !hiddenDeviceIds.value.includes(device.id),
      sortOrder: device.sortOrder ?? index,
    }));
  }

  const resolvedManagedDevices = computed<ShadesManagedDevice[]>(() => {
    if (managedDevices.value.length > 0) return managedDevices.value;
    return buildSeedDevices();
  });

  const compatibleDevices = computed<Device[]>(() => {
    const existingIds = new Set(resolvedManagedDevices.value.map((item) => item.deviceId));
    return autoDetectedShadeDevices.value.filter((device) => !existingIds.has(device.id));
  });

  const shadeEntries = computed<ShadeEntry[]>(() =>
    resolvedManagedDevices.value
      .map((managed) => {
        const device = entitiesStore.getDevice(managed.deviceId);
        return device ? { managed, device, shadeType: detectShadeType(device) } : null;
      })
      .filter((entry): entry is ShadeEntry => Boolean(entry))
      .sort((a, b) => (a.managed.sortOrder ?? 0) - (b.managed.sortOrder ?? 0) || compareDevices(a.device, b.device)),
  );

  const enabledShadeEntries = computed<ShadeEntry[]>(() => shadeEntries.value.filter((entry) => entry.managed.enabled));

  const shadeDevices = computed<Device[]>(() => enabledShadeEntries.value.map((entry) => entry.device));

  const devicesByShadeType = computed<Map<ShadeTypeKey, Device[]>>(() => {
    const map = new Map<ShadeTypeKey, Device[]>();
    for (const type of SHADE_TYPE_ORDER) map.set(type, []);
    for (const entry of enabledShadeEntries.value) {
      map.get(entry.shadeType)?.push(entry.device);
    }
    return map;
  });

  const groupEntries = computed<Array<{ group: ShadeGroup; devices: Device[] }>>(() =>
    [...groups.value]
      .filter((group) => group.enabled)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((group) => ({
        group,
        devices: enabledShadeEntries.value
          .filter(
            (entry) =>
              group.deviceIds.includes(entry.device.id) && supportsGroupCapability(entry.device, group.capability),
          )
          .map((entry) => entry.device)
          .sort(compareDevices),
      })),
  );

  const seasonsSorted = computed(() => [...seasons.value].sort((a, b) => a.sortOrder - b.sortOrder));

  function applySettingsPayload(data: Partial<ShadesSettingsPayload>): void {
    hiddenDeviceIds.value = Array.isArray(data?.hiddenDeviceIds)
      ? data.hiddenDeviceIds.filter((value: unknown): value is string => typeof value === 'string')
      : [];
    managedDevices.value = sanitizeManagedDevices(data?.devices);
    groups.value = sanitizeGroups(data?.groups);
    presets.value = sanitizePresets(data?.presets);
    const payloadSeasons = sanitizeSeasons(data?.seasons);
    if (payloadSeasons.length > 0) seasons.value = payloadSeasons;
    seasonSelectionMode.value = data?.seasonSelectionMode === 'MANUAL' ? 'MANUAL' : 'AUTO';
    manualSeasonId.value = typeof data?.manualSeasonId === 'string' ? data.manualSeasonId : null;
    lastView.value =
      readOneOf(typeof data?.lastView === 'string' ? data.lastView : undefined, SHADE_VIEW_KEYS) ?? 'rooms';
  }

  function buildSettingsPayload(): ShadesSettingsPayload {
    return {
      hiddenDeviceIds: hiddenDeviceIds.value,
      devices: managedDevices.value,
      groups: groups.value,
      presets: presets.value,
      seasons: seasons.value,
      seasonSelectionMode: seasonSelectionMode.value,
      manualSeasonId: manualSeasonId.value,
      lastView: lastView.value,
    };
  }

  async function fetchSettings(): Promise<void> {
    settingsLoading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/shades/settings');
      applySettingsPayload(data);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message ?? tt('Failed to load shades settings', 'Nastavitev senčil ni bilo mogoče naložiti');
      console.error('[shadesStore] fetchSettings error', err);
    } finally {
      settingsLoading.value = false;
    }
  }

  async function saveSettings(): Promise<void> {
    settingsSaving.value = true;
    try {
      const data = await apiFetch('/shades/settings', {
        method: 'PATCH',
        body: JSON.stringify(buildSettingsPayload()),
      });
      applySettingsPayload(data);
    } finally {
      settingsSaving.value = false;
    }
  }

  async function ensureManagedDevices(): Promise<void> {
    if (managedDevices.value.length > 0 || autoDetectedShadeDevices.value.length === 0) return;
    managedDevices.value = buildSeedDevices();
    await saveSettings();
  }

  /**
   * Take the covers the hub reports that the saved settings do not carry into the
   * managed list — the settings page's "Detect Shades" action.
   *
   * `ensureManagedDevices` only ever seeds a hub that has none at all, so a cover
   * the hub reports after that first visit had no way in: the seed saw a non-empty
   * list and returned, and the button looked dead. This is that re-scan. It returns
   * how many were added, so the caller can say what happened instead of leaving the
   * click unanswered.
   */
  async function detectShadeDevices(): Promise<number> {
    const missing = compatibleDevices.value;
    if (missing.length === 0) return 0;

    // The resolved list, not the raw one: a hub that has never been saved yet is
    // read through the seed, and appending to the seed keeps `id`/`deviceId` paired.
    const base = resolvedManagedDevices.value;
    const additions: ShadesManagedDevice[] = missing.map((device, index) => ({
      id: device.id,
      deviceId: device.id,
      enabled: !hiddenDeviceIds.value.includes(device.id),
      sortOrder: base.length + index,
    }));

    managedDevices.value = [...base, ...additions];
    await saveSettings();
    return additions.length;
  }

  async function setLastView(view: ShadesViewMode): Promise<void> {
    if (lastView.value === view) return;
    lastView.value = view;
    await saveSettings();
  }

  async function updateManagedDevice(id: string, changes: Partial<ShadesManagedDevice>): Promise<void> {
    await ensureManagedDevices();
    managedDevices.value = managedDevices.value.map((item) => (item.id === id ? { ...item, ...changes } : item));
    await saveSettings();
  }

  async function createGroup(data: Omit<ShadeGroup, 'id' | 'sortOrder'> & { sortOrder?: number }): Promise<ShadeGroup> {
    const group: ShadeGroup = {
      id: buildId('shade-group'),
      sortOrder: data.sortOrder ?? groups.value.length,
      ...data,
    };
    groups.value = [...groups.value, group].sort((a, b) => a.sortOrder - b.sortOrder);
    await saveSettings();
    return group;
  }

  async function updateGroup(groupId: string, changes: Partial<ShadeGroup>): Promise<void> {
    groups.value = groups.value.map((group) => (group.id === groupId ? { ...group, ...changes } : group));
    await saveSettings();
  }

  async function deleteGroup(groupId: string): Promise<void> {
    groups.value = groups.value.filter((group) => group.id !== groupId);
    presets.value = presets.value.filter((preset) => !(preset.scopeKind === 'group' && preset.scopeId === groupId));
    await saveSettings();
  }

  async function createPreset(
    data: Omit<ShadePreset, 'id' | 'sortOrder'> & { sortOrder?: number },
  ): Promise<ShadePreset> {
    const preset: ShadePreset = {
      id: buildId('shade-preset'),
      sortOrder: data.sortOrder ?? presets.value.length,
      ...data,
    };
    presets.value = [...presets.value, preset].sort((a, b) => a.sortOrder - b.sortOrder);
    await saveSettings();
    return preset;
  }

  async function updatePreset(presetId: string, changes: Partial<ShadePreset>): Promise<void> {
    presets.value = presets.value.map((preset) => (preset.id === presetId ? { ...preset, ...changes } : preset));
    await saveSettings();
  }

  async function deletePreset(presetId: string): Promise<void> {
    presets.value = presets.value.filter((preset) => preset.id !== presetId);
    await saveSettings();
  }

  async function applyCommand(scope: ShadeCommandScope, command: ShadeCommandPayload): Promise<ShadeCommandResult> {
    return apiFetch('/shades/command', {
      method: 'POST',
      body: JSON.stringify({ scope, command }),
    });
  }

  async function applyPreset(preset: ShadePreset): Promise<ShadeCommandResult> {
    return applyCommand({ kind: preset.scopeKind, id: preset.scopeId }, preset.command);
  }

  // ── Seasons ─────────────────────────────────────────────────────────────────

  async function fetchSeasons(): Promise<void> {
    try {
      const data = await apiFetch('/shades/seasons');
      seasons.value = sanitizeSeasons(data?.seasons);
      seasonSelectionMode.value = data?.selectionMode === 'MANUAL' ? 'MANUAL' : 'AUTO';
      manualSeasonId.value = typeof data?.manualSeasonId === 'string' ? data.manualSeasonId : null;
      activeSeasonId.value = typeof data?.runtime?.activeSeasonId === 'string' ? data.runtime.activeSeasonId : null;
    } catch (err) {
      console.error('[shadesStore] fetchSeasons error', err);
    }
  }

  async function createSeason(data: {
    name: string;
    icon?: string;
    startMonth: number;
    startDay: number;
    endMonth: number;
    endDay: number;
  }): Promise<ShadeSeason> {
    const season = await apiFetch('/shades/seasons', { method: 'POST', body: JSON.stringify(data) });
    seasons.value = sanitizeSeasons([...seasons.value, season]);
    return season;
  }

  async function updateSeason(seasonId: string, changes: Partial<ShadeSeason>): Promise<ShadeSeason> {
    const updated = await apiFetch(`/shades/seasons/${seasonId}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
    const sanitized = sanitizeSeasons([updated])[0];
    seasons.value = seasonsSorted.value.map((season) => (season.id === seasonId ? sanitized : season));
    return sanitized;
  }

  async function deleteSeason(seasonId: string): Promise<void> {
    await apiFetch(`/shades/seasons/${seasonId}`, { method: 'DELETE' });
    await fetchSeasons();
  }

  async function reorderSeasons(seasonIds: string[]): Promise<void> {
    const data = await apiFetch('/shades/seasons/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ seasonIds }),
    });
    seasons.value = sanitizeSeasons(data?.seasons);
  }

  async function setSeasonManual(seasonId: string): Promise<void> {
    const data = await apiFetch('/shades/seasons/manual', {
      method: 'POST',
      body: JSON.stringify({ seasonId }),
    });
    seasonSelectionMode.value = 'MANUAL';
    manualSeasonId.value = seasonId;
    activeSeasonId.value = typeof data?.runtime?.activeSeasonId === 'string' ? data.runtime.activeSeasonId : seasonId;
  }

  async function setSeasonAuto(): Promise<void> {
    const data = await apiFetch('/shades/seasons/auto', { method: 'POST' });
    seasonSelectionMode.value = 'AUTO';
    manualSeasonId.value = null;
    activeSeasonId.value = typeof data?.runtime?.activeSeasonId === 'string' ? data.runtime.activeSeasonId : null;
  }

  async function resolveSeason(): Promise<void> {
    const runtime = await apiFetch('/shades/seasons/resolve', { method: 'POST' });
    activeSeasonId.value = typeof runtime?.activeSeasonId === 'string' ? runtime.activeSeasonId : null;
  }

  async function loadAll(): Promise<void> {
    await fetchSettings();
    await fetchSeasons();
  }

  return {
    hiddenDeviceIds,
    managedDevices,
    groups,
    presets,
    seasons,
    seasonsSorted,
    seasonSelectionMode,
    manualSeasonId,
    activeSeasonId,
    activeSeason,
    lastView,
    settingsLoading,
    settingsSaving,
    error,
    autoDetectedShadeDevices,
    resolvedManagedDevices,
    compatibleDevices,
    shadeEntries,
    enabledShadeEntries,
    shadeDevices,
    devicesByShadeType,
    groupEntries,
    fetchSettings,
    saveSettings,
    ensureManagedDevices,
    detectShadeDevices,
    setLastView,
    updateManagedDevice,
    createGroup,
    updateGroup,
    deleteGroup,
    createPreset,
    updatePreset,
    deletePreset,
    applyCommand,
    applyPreset,
    fetchSeasons,
    createSeason,
    updateSeason,
    deleteSeason,
    reorderSeasons,
    setSeasonManual,
    setSeasonAuto,
    resolveSeason,
    loadAll,
  };
});
